import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, LogOut, Activity, Globe, CalendarDays, XCircle, Search, ChevronLeft, ChevronRight, Loader2, DollarSign, Crown, Flag, Gift, Users } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("wraith_token") || ""}` });

const StatCard = ({ icon: Icon, label, value, tid }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid={tid}>
    <div className="flex items-center gap-2 text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">
      <Icon size={14} className="text-[#C0392B]" /> {label}
    </div>
    <p className="text-3xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{value}</p>
  </div>
);

const LoginGate = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("wraith_token", data.access_token);
      onLogin(data.user);
      toast.success("Welcome back, Admin.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
      <form onSubmit={submit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 w-full max-w-sm" data-testid="admin-login-form">
        <div className="flex items-center gap-2 mb-1">
          <Shield size={22} className="text-[#C0392B]" />
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Wraith Panel</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">Restricted area. Admin access only.</p>
        <div className="space-y-3">
          <Input data-testid="admin-email-input" type="email" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input data-testid="admin-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={busy}
          className="mt-5 w-full py-2.5 bg-[#C0392B] hover:bg-[#A93226] disabled:opacity-50 text-white rounded-full font-bold transition-colors flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={16} className="animate-spin" />} Sign in
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const limit = 20;
  const [proTarget, setProTarget] = useState("");
  const [credTarget, setCredTarget] = useState("");
  const [credAmount, setCredAmount] = useState("");
  const [granting, setGranting] = useState(false);
  const [users, setUsers] = useState(null);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [userSearchInput, setUserSearchInput] = useState("");
  const usersLimit = 10;
  const userPages = Math.max(1, Math.ceil(usersTotal / usersLimit));

  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [s, l, p] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
        axios.get(`${API}/admin/logs`, { headers: authHeaders(), params: { page, limit, search } }),
        axios.get(`${API}/admin/payments`, { headers: authHeaders(), params: { page: 1, limit: 20 } }),
      ]);
      setStats(s.data);
      setLogs(l.data.logs);
      setTotal(l.data.total);
      setPayments(p.data.payments);
    } catch {
      toast.error("Failed to load admin data.");
    }
  }, [page, search]);

  useEffect(() => {
    if (user) { loadData(); loadUsers(); }
  }, [user, loadData, loadUsers]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers: authHeaders() });
    } catch (e) {
      void e;
    }
    localStorage.removeItem("wraith_token");
    setUser(false);
  };

  const grantPro = async (revoke) => {
    if (!proTarget.trim()) return toast.error("Enter an email or client ID.");
    setGranting(true);
    try {
      const { data } = await axios.post(`${API}/admin/grant-pro`, { identifier: proTarget.trim(), revoke }, { headers: authHeaders() });
      toast.success(revoke ? `PRO revoked from ${data.clients.length} client(s).` : `PRO granted to ${data.clients.length} client(s).`);
      setProTarget("");
      loadData();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Grant failed.");
    } finally { setGranting(false); }
  };

  const grantCredits = async (e) => {
    e.preventDefault();
    if (!credTarget.trim() || !credAmount) return toast.error("Enter target and amount.");
    setGranting(true);
    try {
      const { data } = await axios.post(`${API}/admin/grant-credits`, { identifier: credTarget.trim(), amount: Number(credAmount) }, { headers: authHeaders() });
      toast.success(`${credAmount} credits added. Total available: ${data.client.total_available}.`);
      setCredTarget(""); setCredAmount("");
      loadData();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Grant failed.");
    } finally { setGranting(false); }
  };

  const loadUsers = useCallback(async (page = 1, search = userSearch) => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { params: { page, limit: usersLimit, search }, headers: authHeaders() });
      setUsers(data.users); setUsersPage(data.page); setUsersTotal(data.total);
    } catch { setUsers([]); }
  }, [userSearch]);

  const searchUsers = (e) => { e.preventDefault(); setUserSearch(userSearchInput); setUsersPage(1); loadUsers(1, userSearchInput); };

  const selectUser = (u) => {
    const id = u.email || u.client_id;
    setProTarget(id); setCredTarget(id);
    toast.success(`Selected ${u.email || (u.client_id.slice(0, 8) + "…")}`);
    document.getElementById("grant-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (checking) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><Loader2 className="animate-spin text-[#C0392B]" size={28} /></div>;
  if (!user) return <LoginGate onLogin={setUser} />;

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black flex items-center gap-2.5" style={{ fontFamily: "'Playfair Display', serif" }}>
              <Shield size={30} className="text-[#C0392B]" /> Wraith Admin
            </h1>
            <p className="text-sm text-gray-500 mt-1">Monitoring every scan and client on Wraith Checker.</p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:border-[#C0392B] hover:text-[#C0392B] transition-colors"
          >
            <LogOut size={15} /> Logout
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            <StatCard icon={Activity} label="Total Scans" value={stats.total_scans} tid="stat-total-scans" />
            <StatCard icon={Globe} label="Domains Checked" value={stats.total_domains} tid="stat-total-domains" />
            <StatCard icon={CalendarDays} label="Scans Today" value={stats.scans_today} tid="stat-scans-today" />
            <StatCard icon={XCircle} label="Failed Scans" value={stats.failed_scans} tid="stat-failed-scans" />
            <StatCard icon={DollarSign} label="Revenue (USD)" value={`$${(stats.total_revenue || 0).toFixed(2)}`} tid="stat-revenue" />
            <StatCard icon={Activity} label="Payments" value={stats.completed_payments || 0} tid="stat-payments" />
            <StatCard icon={Crown} label="PRO Clients" value={stats.pro_clients || 0} tid="stat-pro-clients" />
            <StatCard icon={Flag} label="Flagged Clients" value={stats.flagged_clients_count ?? (stats.flagged_clients || []).length} tid="stat-flagged" />
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm mb-10" data-testid="users-section">
          <h3 className="font-bold text-xl mb-1 flex items-center gap-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>
            <Users size={18} className="text-[#C0392B]" /> Users & Clients
          </h3>
          <p className="text-sm text-gray-500 mb-4">Search clients by email or client ID, then select to grant PRO or credits.</p>
          <form onSubmit={searchUsers} className="flex gap-2 mb-4">
            <Input data-testid="user-search-input" placeholder="Search email or client ID…" value={userSearchInput} onChange={(e) => setUserSearchInput(e.target.value)} />
            <button type="submit" data-testid="user-search-btn" className="px-4 py-2 text-sm font-bold bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors flex items-center gap-1.5"><Search size={14} /> Search</button>
            {userSearch && <button type="button" onClick={() => { setUserSearch(""); setUserSearchInput(""); setUsersPage(1); loadUsers(1, ""); }} className="px-3 py-2 text-sm font-semibold text-gray-500 border border-gray-200 rounded-full hover:border-gray-300">Clear</button>}
          </form>
          {users === null ? (
            <div className="flex items-center justify-center py-6"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
          ) : users.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No clients found.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-gray-100">
                      <th className="py-2 pr-3 font-semibold">Email / Client</th>
                      <th className="py-2 px-3 font-semibold">PRO</th>
                      <th className="py-2 px-3 font-semibold">Credits</th>
                      <th className="py-2 px-3 font-semibold">Last seen</th>
                      <th className="py-2 pl-3 font-semibold text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.client_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="py-2.5 pr-3">
                          <div className="font-medium text-gray-900 truncate max-w-[180px]">{u.email || <span className="font-mono text-gray-400">{u.client_id.slice(0, 12)}…</span>}</div>
                          {u.name && <div className="text-xs text-gray-400 truncate max-w-[180px]">{u.name}</div>}
                        </td>
                        <td className="py-2.5 px-3">{u.is_pro ? <span className="inline-flex items-center gap-1 text-xs font-bold text-green-600"><Crown size={11} /> PRO</span> : <span className="text-gray-300">—</span>}</td>
                        <td className="py-2.5 px-3 text-gray-700">{u.purchased_credits}</td>
                        <td className="py-2.5 px-3 text-xs text-gray-400">{u.last_seen ? new Date(u.last_seen).toLocaleDateString() : "—"}</td>
                        <td className="py-2.5 pl-3 text-right"><button data-testid={`select-user-${u.client_id}`} onClick={() => selectUser(u)} className="text-xs font-bold text-[#C0392B] hover:underline">Select</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {userPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-4 text-sm">
                  <button disabled={usersPage <= 1} onClick={() => loadUsers(usersPage - 1)} className="p-1.5 rounded-full border border-gray-200 disabled:opacity-30 hover:border-gray-300"><ChevronLeft size={14} /></button>
                  <span className="text-gray-500">{usersPage} / {userPages}</span>
                  <button disabled={usersPage >= userPages} onClick={() => loadUsers(usersPage + 1)} className="p-1.5 rounded-full border border-gray-200 disabled:opacity-30 hover:border-gray-300"><ChevronRight size={14} /></button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm mb-10" data-testid="grant-section">
          <h3 className="font-bold text-xl mb-1 flex items-center gap-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>
            <Gift size={18} className="text-[#C0392B]" /> Grant Plans & Credits
          </h3>
          <p className="text-sm text-gray-500 mb-5">Manually grant PRO or add credits to any client or Google user (by email).</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <form onSubmit={(e) => { e.preventDefault(); grantPro(false); }} className="space-y-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Grant / Revoke PRO</label>
              <Input data-testid="grant-pro-input" placeholder="Email or client ID" value={proTarget} onChange={(e) => setProTarget(e.target.value)} />
              <div className="flex gap-2">
                <button type="submit" disabled={granting} data-testid="grant-pro-btn" className="flex-1 py-2.5 text-sm font-bold bg-[#C0392B] hover:bg-[#A93226] disabled:opacity-50 text-white rounded-full transition-colors flex items-center justify-center gap-1.5"><Crown size={14} /> Grant PRO</button>
                <button type="button" disabled={granting} data-testid="revoke-pro-btn" onClick={() => grantPro(true)} className="px-4 py-2.5 text-sm font-bold border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600 rounded-full transition-colors">Revoke</button>
              </div>
            </form>
            <form onSubmit={grantCredits} className="space-y-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Give Credits</label>
              <Input data-testid="grant-credits-input" placeholder="Email or client ID" value={credTarget} onChange={(e) => setCredTarget(e.target.value)} />
              <div className="flex gap-2">
                <Input data-testid="grant-credits-amount" type="number" min={1} max={100000} placeholder="Amount" value={credAmount} onChange={(e) => setCredAmount(e.target.value)} className="w-40" />
                <button type="submit" disabled={granting} data-testid="grant-credits-btn" className="flex-1 py-2.5 text-sm font-bold bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white rounded-full transition-colors flex items-center justify-center gap-1.5"><Gift size={14} /> Give Credits</button>
              </div>
            </form>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="top-domains-card">
              <h3 className="font-bold text-lg mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Top Checked Domains</h3>
              {stats.top_domains.length === 0 && <p className="text-sm text-gray-400">No data yet.</p>}
              <div className="space-y-2">
                {stats.top_domains.map((d) => (
                  <div key={d.domain} className="flex justify-between items-center text-sm">
                    <span className="text-gray-700 font-medium">{d.domain}</span>
                    <span className="px-2 py-0.5 rounded-full bg-[#FADBD8]/60 text-[#922B21] text-xs font-bold">{d.count}×</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="top-clients-card">
              <h3 className="font-bold text-lg mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Top Clients (by IP)</h3>
              {stats.top_clients.length === 0 && <p className="text-sm text-gray-400">No data yet.</p>}
              <div className="space-y-2">
                {stats.top_clients.map((c) => (
                  <div key={c.ip} className="flex justify-between items-center text-sm">
                    <span className="text-gray-700 font-mono">{c.ip}</span>
                    <span className="text-gray-500 text-xs">{c.scans} scans · {c.domains} domains</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="flagged-clients-card">
              <h3 className="font-bold text-lg mb-3 flex items-center gap-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                <Flag size={16} className="text-[#C0392B]" /> Flagged Clients
              </h3>
              {(stats.flagged_clients || []).length === 0 && <p className="text-sm text-gray-400">No suspicious activity detected.</p>}
              <div className="space-y-3">
                {(stats.flagged_clients || []).map((c) => (
                  <div key={c.client_id} className="text-sm border-b border-gray-100 pb-2 last:border-0">
                    <div className="flex justify-between items-center">
                      <span className="font-mono text-xs text-gray-600 truncate max-w-[160px]" title={c.client_id}>{c.client_id}</span>
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-bold">{c.flags} flags</span>
                    </div>
                    {(c.flag_reasons || []).slice(-1).map((r, i) => (
                      <p key={i} className="text-xs text-gray-400 mt-1 truncate" title={r.reason}>{r.reason}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-10" data-testid="payments-section">
          <div className="p-5 border-b border-gray-100">
            <h3 className="font-bold text-xl flex items-center gap-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>
              <DollarSign size={18} className="text-[#C0392B]" /> Payments
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[700px]">
              <thead>
                <tr>
                  {["Time", "Product", "Amount", "Client", "Order ID", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-sm" data-testid="payments-empty">No payments yet.</td></tr>
                )}
                {payments.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors" data-testid={`payment-row-${p.id}`}>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap border-b border-gray-100">{new Date(p.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.product === "pro" ? "bg-[#FADBD8]/70 text-[#922B21]" : "bg-amber-100 text-amber-800"}`}>
                        {p.product === "pro" ? "PRO Upgrade" : `+${p.credits || 100} Credits`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-800 border-b border-gray-100">${p.amount.toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500 max-w-[160px] truncate border-b border-gray-100" title={p.client_id}>{p.client_id}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500 border-b border-gray-100">{p.order_id}</td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.status === "completed" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="logs-section">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-5 border-b border-gray-100">
            <h3 className="font-bold text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>Scan Logs</h3>
            <div className="relative w-full sm:w-72">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="logs-search-input"
                placeholder="Search domain or IP…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[900px]">
              <thead>
                <tr>
                  {["Time", "Mode", "Domains", "Count", "IP", "Status", "Duration", "User Agent"].map((h) => (
                    <th key={h} className="px-4 py-3 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400 text-sm" data-testid="logs-empty">No logs yet.</td></tr>
                )}
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-testid={`log-row-${log.id}`}>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap border-b border-gray-100">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.mode === "bulk" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"}`}>{log.mode}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-[280px] truncate border-b border-gray-100" title={log.domains.join(", ")}>{log.domains.join(", ")}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">{log.domain_count}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-600 border-b border-gray-100">{log.ip}</td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${log.success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {log.success ? "Success" : "Failed"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 border-b border-gray-100">{(log.duration_ms / 1000).toFixed(1)}s</td>
                    <td className="px-4 py-3 text-xs text-gray-400 max-w-[200px] truncate border-b border-gray-100" title={log.user_agent}>{log.user_agent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between p-4 border-t border-gray-100">
            <p className="text-xs text-gray-500">{total} total logs</p>
            <div className="flex items-center gap-2">
              <button data-testid="logs-prev-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 rounded-full border border-gray-200 disabled:opacity-40 hover:border-[#C0392B] transition-colors"><ChevronLeft size={15} /></button>
              <span className="text-sm text-gray-600">{page} / {pages}</span>
              <button data-testid="logs-next-btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 rounded-full border border-gray-200 disabled:opacity-40 hover:border-[#C0392B] transition-colors"><ChevronRight size={15} /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
